import React, { FC, useMemo } from 'react';
import { profileActivityWithOrganization, systemActivityTypesTableConfigs } from './constants/SystemActivityConstants';

import { ChevronDown } from '@td-client-frontend/icons';
import { Link } from 'react-router-dom';
import { ReactTable } from '@td-client-frontend/common';
import clsx from 'clsx';

const Expand: FC<{ expanded: boolean }> = ({ expanded }) => {
  return (
    <ChevronDown className={clsx('w-5 h-5 transform inline ml-2', expanded ? '-rotate-180' : 'rotate-0')} aria-hidden />
  );
};
const isColumnVisible = (data: any, propName: string) =>
  data.some((row: Record<string, any>) =>
    Object.keys(row).some((key: string) =>
      typeof row[key] === 'object' ? isColumnVisible(row[key], propName) : key === propName && row[key]
    )
  );

export const SystemActivityTable: FC<{ data: any; selectedActivityType: string }> = ({
  data,
  selectedActivityType
}) => {
  const isActionsVisible: boolean = data.some((row: Record<string, any>) => row.hasModalInfo);

  const activityColumnSettings: Record<string, any> = useMemo(() => {
    return {
      general: [
        {
          id: 'expander',
          Header: '',
          Cell: ({ row }: any) => {
            return row.canExpand ? (
              <span className="w-12" {...row.getToggleRowExpandedProps({})}>
                <Expand expanded={!!row.isExpanded} />
              </span>
            ) : null;
          },
          isVisible: true,
          width: 20
        },
        {
          id: 'activityType',
          Header: 'Activity type',
          accessor: 'activityType',
          isVisible: true
        },
        {
          id: 'date',
          Header: 'Date',
          canSort: true,
          accessor: 'createdAt',
          isVisible: true
        },
        {
          id: 'user',
          Header: 'User name',
          accessor: 'user',
          isVisible: isColumnVisible(data, 'user')
        }
      ],
      members: [
        {
          id: 'member',
          Header: 'Member',
          canSort: true,
          accessor: 'member',
          isVisible: true
        },
        {
          id: 'primaryMember',
          Header: 'Primary Member',
          canSort: true,
          accessor: 'primaryMember',
          isVisible: true
        }
      ],
      roles: [
        {
          id: 'roleNm',
          Header: 'Role name',
          accessor: 'roleNm',
          isVisible: isColumnVisible(data, 'roleNm')
        }
      ],
      marketing: [
        {
          id: 'profileOrOrgName',
          Header: profileActivityWithOrganization.includes(selectedActivityType) ? 'Organization name' : 'Profile name',
          accessor: 'profileOrOrgName',
          isVisible: isColumnVisible(data, 'profileOrOrgName')
        }
      ],
      isForcedLogout: [
        {
          id: 'forced',
          Header: 'Forced',
          accessor: 'isForcedLogout',
          isVisible: isColumnVisible(data, 'isForcedLogout')
        }
      ],
      affectedUser: [
        {
          id: 'affectedUser',
          Header: 'Affected user',
          accessor: 'affectedUser',
          isVisible: isColumnVisible(data, 'affectedUser')
        }
      ],
      view: {
        id: 'view',
        Header: 'Action',
        Cell: ({ row }: any) =>
          !row.subRows?.length &&
          row.original.hasModalInfo && (
            <Link
              className="flex text-primary-500 hover:text-primary-70"
              to={`/system-activity-log/${row.original._id}`}
            >
              View
            </Link>
          ),
        isVisible: isActionsVisible,
        width: 50
      },
      empty: []
    };
  }, [data, isActionsVisible, selectedActivityType]);

  const activitiesSubRowsConfig = useMemo(() => {
    return [
      {
        id: 'prop',
        Header: 'Section',
        accessor: 'prop',
        isVisible: isColumnVisible(data, 'prop')
      },
      {
        id: 'field',
        Header: 'Field',
        accessor: 'field',
        isVisible: isColumnVisible(data, 'field')
      },
      {
        id: 'before',
        Header: 'Old Value',
        accessor: 'before',
        isVisible: isColumnVisible(data, 'before')
      },
      {
        id: 'after',
        Header: 'New Value',
        accessor: 'after',
        isVisible: isColumnVisible(data, 'after')
      }
    ];
  }, [data]);

  const activitiesColumnConfig = useMemo(() => {
    const configKey: string =
      Object.keys(systemActivityTypesTableConfigs).find((key) => key.includes(selectedActivityType)) || 'default';
    return [
      ...activityColumnSettings.general,
      ...activityColumnSettings[systemActivityTypesTableConfigs[configKey]],
      activityColumnSettings.view
    ];
  }, [activityColumnSettings, selectedActivityType]);

  return (
    <ReactTable
      columns={activitiesColumnConfig}
      data={data}
      subColumns={activitiesSubRowsConfig}
      expandSubRows={false}
    />
  );
};
